import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  FormControlLabel,
  Paper,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Link,
} from '@mui/material';
import {
  collection,
  doc,
  getDoc,
  limit,
  onSnapshot,
  orderBy,
  query,
  where,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../firebase';
import { isAdminRole } from '../lib/rolePermissions';
import { dashboardTicketPath } from '../lib/dashboardRoutes';
import {
  TICKET_BRIEFING_COLLECTION,
  formatBriefingTimestamp,
  formatBriefingUsd,
  formatWallClock,
} from '../lib/ticketBriefing';
import { Link as RouterLink } from 'react-router-dom';

const extractTicketBriefingFn = httpsCallable(functions, 'extractTicketBriefing');
const backfillTicketBriefingsFn = httpsCallable(functions, 'backfillTicketBriefings');

/**
 * Minimal internal spot-check: briefing list + ticket/briefing side-by-side.
 * Admin-only; supports accuracy gating before Reports Phase 2.
 *
 * @param {{ userData?: { companyId?: string, role?: string } | null }} props
 */
export default function TicketBriefingSpotCheck({ userData }) {
  const companyId =
    typeof userData?.companyId === 'string' ? userData.companyId.trim() : '';
  const allowed = isAdminRole(userData?.role) && Boolean(companyId);

  const [briefings, setBriefings] = useState([]);
  const [listLoading, setListLoading] = useState(allowed);
  const [listError, setListError] = useState('');
  const [selectedId, setSelectedId] = useState('');
  const [ticket, setTicket] = useState(null);
  const [ticketLoading, setTicketLoading] = useState(false);
  const [jumpId, setJumpId] = useState('');
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState('');
  const [actionInfo, setActionInfo] = useState('');
  const [skipLlm, setSkipLlm] = useState(false);

  const selectedBriefing = useMemo(
    () => briefings.find((b) => b.id === selectedId) || null,
    [briefings, selectedId],
  );

  useEffect(() => {
    if (!allowed) {
      setBriefings([]);
      setListLoading(false);
      setListError('');
      return undefined;
    }

    setListLoading(true);
    setListError('');
    const q = query(
      collection(db, TICKET_BRIEFING_COLLECTION),
      where('companyId', '==', companyId),
      orderBy('extractedAt', 'desc'),
      limit(40),
    );

    const unsub = onSnapshot(
      q,
      (snap) => {
        const rows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        setBriefings(rows);
        setListLoading(false);
        setSelectedId((prev) => {
          if (prev && rows.some((r) => r.id === prev)) return prev;
          return rows[0]?.id || '';
        });
      },
      (err) => {
        console.error('TicketBriefingSpotCheck list:', err);
        setListError(
          err?.message ||
            'Failed to load briefings. Deploy firestore indexes if prompted.',
        );
        setBriefings([]);
        setListLoading(false);
      },
    );
    return () => unsub();
  }, [allowed, companyId]);

  useEffect(() => {
    if (!allowed || !selectedId) {
      setTicket(null);
      return undefined;
    }
    let cancelled = false;
    setTicketLoading(true);
    getDoc(doc(db, 'tickets', selectedId))
      .then((snap) => {
        if (cancelled) return;
        setTicket(snap.exists() ? { id: snap.id, ...snap.data() } : null);
        setTicketLoading(false);
      })
      .catch((err) => {
        if (cancelled) return;
        console.error('TicketBriefingSpotCheck ticket:', err);
        setTicket(null);
        setTicketLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [allowed, selectedId]);

  const handleJump = async () => {
    const id = jumpId.trim();
    if (!id) return;
    setActionError('');
    setActionInfo('');
    setBusy(true);
    try {
      const briefingSnap = await getDoc(doc(db, TICKET_BRIEFING_COLLECTION, id));
      if (briefingSnap.exists()) {
        const data = briefingSnap.data() || {};
        if (data.companyId && data.companyId !== companyId) {
          setActionError('That briefing belongs to another company.');
          return;
        }
        setSelectedId(id);
        if (!briefings.some((b) => b.id === id)) {
          setBriefings((prev) => [{ id, ...data }, ...prev]);
        }
        setActionInfo(`Loaded briefing ${id}.`);
        return;
      }
      await extractTicketBriefingFn({ ticketId: id, skipLlm });
      setSelectedId(id);
      setActionInfo(`Extracted briefing for ${id}.`);
    } catch (err) {
      console.error('TicketBriefingSpotCheck jump:', err);
      setActionError(err?.message || 'Jump / extract failed.');
    } finally {
      setBusy(false);
    }
  };

  const handleReExtract = async () => {
    if (!selectedId) return;
    setActionError('');
    setActionInfo('');
    setBusy(true);
    try {
      const res = await extractTicketBriefingFn({
        ticketId: selectedId,
        skipLlm,
      });
      setActionInfo(
        `Re-extracted (${res.data?.mode || '?'}) · disposition ${res.data?.disposition || '?'}`,
      );
    } catch (err) {
      console.error('TicketBriefingSpotCheck re-extract:', err);
      setActionError(err?.message || 'Re-extract failed.');
    } finally {
      setBusy(false);
    }
  };

  const handleBackfill = async () => {
    setActionError('');
    setActionInfo('');
    setBusy(true);
    try {
      const res = await backfillTicketBriefingsFn({
        limit: 25,
        skipLlm,
        force: false,
      });
      const processed = res.data?.processed ?? 0;
      const skipped = (res.data?.results || []).filter((r) => r.skipped).length;
      setActionInfo(
        `Backfill done: processed ${processed}, already-existed skips ${skipped}.`,
      );
    } catch (err) {
      console.error('TicketBriefingSpotCheck backfill:', err);
      setActionError(err?.message || 'Backfill failed.');
    } finally {
      setBusy(false);
    }
  };

  if (!allowed) {
    return (
      <Alert severity="warning">
        Ticket briefing spot-check is available to company admins only.
      </Alert>
    );
  }

  const effort = selectedBriefing?.effort || {};
  const outcome = selectedBriefing?.outcome || {};
  const events = Array.isArray(selectedBriefing?.frictionEvents)
    ? selectedBriefing.frictionEvents
    : [];

  return (
    <Box sx={{ p: { xs: 1.5, md: 2 }, maxWidth: 1400, mx: 'auto' }}>
      <Stack spacing={2}>
        <Box>
          <Typography variant="h5" fontWeight={700}>
            Ticket briefing spot-check
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Phase 1 accuracy gate — compare extracted briefing vs ticket thread.
            Gold set target: ~15–25 closed tickets spanning clean-approve,
            strikeout, budget trip, multi-reject, cross-repo, human-stop.
            Treat <code>hallucinated_narration</code> as exploratory for now —
            gold-set validated on Gemini vs Claude (see{' '}
            <Link
              href="https://github.com/tatotron/shifty-pm-v1/blob/main/docs/ai-architecture/07-hallucinated-narration-validation.md"
              target="_blank"
              rel="noopener noreferrer"
            >
              hallucinated-narration validation notes
            </Link>
            ); do not use it as a reliable Reports rollup signal yet.
          </Typography>
        </Box>

        <Paper variant="outlined" sx={{ p: 2 }}>
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={1.5}
            alignItems={{ md: 'center' }}
          >
            <TextField
              size="small"
              label="Ticket / briefing ID"
              value={jumpId}
              onChange={(e) => setJumpId(e.target.value)}
              sx={{ minWidth: 280 }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') void handleJump();
              }}
            />
            <Button
              variant="contained"
              disabled={busy || !jumpId.trim()}
              onClick={() => void handleJump()}
            >
              Jump / extract
            </Button>
            <Button
              variant="outlined"
              disabled={busy || !selectedId}
              onClick={() => void handleReExtract()}
            >
              Re-extract selected
            </Button>
            <Button
              variant="outlined"
              disabled={busy}
              onClick={() => void handleBackfill()}
            >
              Backfill missing (25)
            </Button>
            <FormControlLabel
              control={
                <Switch
                  checked={skipLlm}
                  onChange={(e) => setSkipLlm(e.target.checked)}
                />
              }
              label="Heuristic only (skip LLM)"
            />
            {busy ? <CircularProgress size={22} /> : null}
          </Stack>
          {actionError ? (
            <Alert severity="error" sx={{ mt: 1.5 }}>
              {actionError}
            </Alert>
          ) : null}
          {actionInfo ? (
            <Alert severity="success" sx={{ mt: 1.5 }}>
              {actionInfo}
            </Alert>
          ) : null}
        </Paper>

        {listError ? <Alert severity="error">{listError}</Alert> : null}

        <Stack direction={{ xs: 'column', lg: 'row' }} spacing={2} alignItems="stretch">
          <Paper variant="outlined" sx={{ flex: '0 0 320px', p: 1.5, maxHeight: 640, overflow: 'auto' }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Recent briefings
            </Typography>
            {listLoading ? (
              <CircularProgress size={24} />
            ) : briefings.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                None yet — resolve a ticket or run backfill.
              </Typography>
            ) : (
              <Stack spacing={0.75}>
                {briefings.map((b) => (
                  <Box
                    key={b.id}
                    onClick={() => setSelectedId(b.id)}
                    sx={{
                      p: 1,
                      borderRadius: 1,
                      cursor: 'pointer',
                      bgcolor:
                        b.id === selectedId ? 'action.selected' : 'transparent',
                      '&:hover': { bgcolor: 'action.hover' },
                    }}
                  >
                    <Typography variant="body2" fontWeight={600} noWrap>
                      {b.id}
                    </Typography>
                    <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                      <Chip
                        size="small"
                        label={b.outcome?.disposition || '?'}
                        color={
                          b.outcome?.disposition === 'resolved'
                            ? 'success'
                            : b.outcome?.disposition === 'abandoned'
                              ? 'warning'
                              : 'default'
                        }
                      />
                      <Chip size="small" variant="outlined" label={b.extractor?.mode || '?'} />
                    </Stack>
                    <Typography variant="caption" color="text.secondary">
                      {formatBriefingTimestamp(b.extractedAt)}
                    </Typography>
                  </Box>
                ))}
              </Stack>
            )}
          </Paper>

          <Paper variant="outlined" sx={{ flex: 1, p: 2, minHeight: 420 }}>
            {!selectedId ? (
              <Typography color="text.secondary">Select a briefing.</Typography>
            ) : (
              <Stack spacing={2}>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Typography variant="h6" fontWeight={700}>
                    Briefing · {selectedId}
                  </Typography>
                  <Button
                    component={RouterLink}
                    to={dashboardTicketPath(selectedId)}
                    size="small"
                  >
                    Open ticket
                  </Button>
                </Stack>

                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  <Chip label={`disposition: ${outcome.disposition || '—'}`} />
                  <Chip
                    label={`humanInterventionBeyondReview: ${
                      outcome.humanInterventionBeyondReview ? 'yes' : 'no'
                    }`}
                    color={outcome.humanInterventionBeyondReview ? 'warning' : 'default'}
                  />
                  <Chip label={`resolutionPath: ${selectedBriefing?.resolutionPath || '—'}`} />
                  <Chip
                    variant="outlined"
                    label={
                      selectedBriefing?.scope?.targetRepo?.owner &&
                      selectedBriefing?.scope?.targetRepo?.repoName
                        ? `repo: ${selectedBriefing.scope.targetRepo.owner}/${selectedBriefing.scope.targetRepo.repoName}`
                        : 'repo: —'
                    }
                  />
                  <Chip
                    variant="outlined"
                    label={`extracted ${formatBriefingTimestamp(selectedBriefing?.extractedAt)}`}
                  />
                </Stack>

                <Box>
                  <Typography variant="subtitle2">Effort</Typography>
                  <Typography variant="body2">
                    {effort.aiCallCount ?? 0} calls · {formatBriefingUsd(effort.aiSpendUsd)} ·{' '}
                    wall {formatWallClock(outcome.wallClockMs)} · wakes≈{' '}
                    {effort.wakeSessionCountApprox ?? '—'} ({effort.wakeSessionConfidence}) ·
                    sandboxes {effort.sandboxCounts?.approved ?? 0} approved /{' '}
                    {effort.sandboxCounts?.rejected ?? 0} rejected /{' '}
                    {effort.sandboxCounts?.created ?? 0} created
                  </Typography>
                </Box>

                <Box>
                  <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                    Friction events
                  </Typography>
                  {events.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                      None tagged.
                    </Typography>
                  ) : (
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Tag</TableCell>
                          <TableCell>Root cause</TableCell>
                          <TableCell>Conf.</TableCell>
                          <TableCell>Source</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {events.map((ev, idx) => (
                          <TableRow key={`${ev.tag}-${idx}`}>
                            <TableCell>
                              {ev.tag}
                              {ev.subReason ? (
                                <Typography variant="caption" display="block" color="text.secondary">
                                  {ev.subReason}
                                </Typography>
                              ) : null}
                            </TableCell>
                            <TableCell>{ev.rootCause || '—'}</TableCell>
                            <TableCell>{ev.confidence || '—'}</TableCell>
                            <TableCell>{ev.source || '—'}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                </Box>

                <Divider />

                <Box>
                  <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                    Ticket snapshot (for manual compare)
                  </Typography>
                  {ticketLoading ? (
                    <CircularProgress size={22} />
                  ) : !ticket ? (
                    <Typography variant="body2" color="text.secondary">
                      Ticket not found or not readable.
                    </Typography>
                  ) : (
                    <Stack spacing={0.5}>
                      <Typography variant="body2">
                        <strong>{ticket.title || '(untitled)'}</strong> · status{' '}
                        {ticket.status} · agent {ticket.assigneeAgentName || ticket.assigneeAgentId || '—'}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        strikeout reason: {ticket.agentSuspendReason || '—'} ·
                        budgetExceeded: {ticket.aiBudgetExceeded ? 'yes' : 'no'} ·
                        humanStopped: {ticket.humanStopped ? 'yes' : 'no'} ·
                        rejectionCount: {ticket.rejectionCount ?? 0}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          maxHeight: 160,
                          overflow: 'auto',
                          whiteSpace: 'pre-wrap',
                          fontFamily: 'monospace',
                          fontSize: 12,
                          bgcolor: 'action.hover',
                          p: 1,
                          borderRadius: 1,
                        }}
                      >
                        {(ticket.body || ticket.description || '').slice(0, 2000) || '(empty body)'}
                      </Typography>
                    </Stack>
                  )}
                </Box>
              </Stack>
            )}
          </Paper>
        </Stack>
      </Stack>
    </Box>
  );
}
